import {type RunWithHistory} from '../../../types/run';
import {type MediaBrowserPanelConfig} from '../common';
import {runHasMediaKey} from './runUtils';

export type CompareModeRunPair = {
  firstRunName: string;
  secondRunName: string;
};

function getNextMatchingRun(
  runs: RunWithHistory[],
  startIndex: number,
  mediaKey?: string | null,
  excludedRunName?: string | null
): RunWithHistory | null {
  if (runs.length === 0) {
    return null;
  }

  for (let offset = 1; offset <= runs.length; offset++) {
    const run = runs[(startIndex + offset) % runs.length];
    if (run.name !== excludedRunName && runHasMediaKey(run, mediaKey)) {
      return run;
    }
  }

  return null;
}

export function getCompareModeRunPair(
  runs: RunWithHistory[],
  currentRunName?: string | null,
  mediaKey?: string | null
): CompareModeRunPair | null {
  const currentRunIndex =
    currentRunName != null
      ? runs.findIndex(run => run.name === currentRunName)
      : -1;

  if (currentRunName != null) {
    const currentRun = runs[currentRunIndex];
    if (currentRun != null && !runHasMediaKey(currentRun, mediaKey)) {
      return null;
    }
    const secondRun = getNextMatchingRun(
      runs,
      currentRunIndex >= 0 ? currentRunIndex : -1,
      mediaKey,
      currentRunName
    );

    if (secondRun == null) {
      return null;
    }

    return {
      firstRunName: currentRunName,
      secondRunName: secondRun.name,
    };
  }

  const firstRun = runs.find(run => runHasMediaKey(run, mediaKey));
  if (firstRun == null) {
    return null;
  }

  const firstRunIndex = runs.findIndex(run => run.name === firstRun.name);
  const secondRun = getNextMatchingRun(
    runs,
    firstRunIndex,
    mediaKey,
    firstRun.name
  );
  if (secondRun == null) {
    return null;
  }

  return {
    firstRunName: firstRun.name,
    secondRunName: secondRun.name,
  };
}

export function getCompareModeEntryConfig(
  firstRunName: string,
  secondRunName: string,
  mediaKey?: string
): Partial<MediaBrowserPanelConfig> {
  return {
    mode: 'compare',
    columnCount: 2,
    actualSize: false,
    fitToDimension: false,
    videoActualSize: false,
    ...(mediaKey != null ? {mediaKeys: [mediaKey]} : {}),
    compareSettings: {
      columns: [{run: firstRunName}, {run: secondRunName}],
      tileControls: {
        run: 'individual',
        mediaKey: 'linked',
        step: 'linked',
        index: 'linked',
      },
    },
  };
}
